/**
 * Decide cine poate edita intervalele IP în UI, per câmp ('ssm' sau 'su').
 *
 * Reguli structurale (cine are dreptul pe firmă):
 *   - SUPER_ADMIN, SSM_COMPANY_ADMIN, SSM_INSTRUCTOR — întotdeauna
 *   - COMPANY_ADMIN — doar dacă firma e standalone
 *   - EMPLOYEE manager departament (CLM) — doar dacă firma e standalone
 *
 * Reguli pe câmp:
 *   - 'ssm': doar SSM_INSTRUCTOR implică automat SSM (rolul = "instructor SSM").
 *           SSM_COMPANY_ADMIN poate fi doar șeful firmei → necesită flag `isSSMInstructor`.
 *           La fel COMPANY_ADMIN și EMPLOYEE.
 *   - 'su':  toți necesită flag `isCadruTehnicPSI` (niciun rol nu implică PSI)
 *   - CLM și SUPER_ADMIN = exempții (pot ambele)
 */

// Roluri din firma SSM (relevante pentru regula structurală)
const SSM_ROLES = new Set(['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR']);

const fieldAllowedForUser = (user, field) => {
  if (field === 'ssm') {
    if (user.role === 'SSM_INSTRUCTOR') return true; // doar acest rol implică SSM
    return !!user.isSSMInstructor;
  }
  if (field === 'su') {
    return !!user.isCadruTehnicPSI;
  }
  return false;
};

/**
 * Verifică dacă firma oferă serviciul aferent câmpului ('ssm' sau 'su').
 * Default true dacă firma nu are câmpurile setate (compatibilitate cu firme vechi).
 */
const companyOffersField = (company, field) => {
  if (!company) return true; // necunoscut → permisiv (validarea backend va decide)
  if (field === 'ssm') return company.offersSSM !== false;
  if (field === 'su') return company.offersSU !== false;
  return false;
};

/**
 * Pentru context "propria firmă" (COMPANY_ADMIN, CLM care văd doar firma lor).
 * `user` din AuthContext include offersSSM/offersSU prin /auth/me dacă au fost setate la login.
 * Dacă nu sunt prezente, presupunem că firma le oferă (compatibilitate).
 */
export const canEditOwnIPField = (user, field) => {
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;

  // Firma utilizatorului nu oferă serviciul → nu se poate edita
  if (field === 'ssm' && user.offersSSM === false) return false;
  if (field === 'su' && user.offersSU === false) return false;

  if (SSM_ROLES.has(user.role)) return fieldAllowedForUser(user, field);

  // Roluri client: firma trebuie să fie standalone
  if (user.parentCompanyId) return false;

  if (user.role === 'COMPANY_ADMIN') return fieldAllowedForUser(user, field);
  if (user.role === 'EMPLOYEE' && user.managesDepartment) return true; // CLM exempție
  return false;
};

/**
 * Pentru context per-angajat (când vezi date din mai multe firme — ex. SSM admin).
 * `employeeCompany` poate include offersSSM/offersSU.
 */
export const canEditIPFieldForEmployeeCompany = (user, employeeCompany, field) => {
  if (!user) return false;

  // Firma angajatului nu oferă serviciul → nimeni nu îl poate edita pentru ei
  if (!companyOffersField(employeeCompany, field)) return false;

  if (user.role === 'SUPER_ADMIN') return true;

  if (SSM_ROLES.has(user.role)) return fieldAllowedForUser(user, field);

  if (!employeeCompany) return false;
  if (employeeCompany.parentCompanyId) return false;
  if (employeeCompany.id !== user.companyId) return false;

  if (user.role === 'COMPANY_ADMIN') return fieldAllowedForUser(user, field);
  if (user.role === 'EMPLOYEE' && user.managesDepartment) return true;
  return false;
};

/** Helpers de convenință: poate edita cel puțin un câmp? */
export const canEditAnyIP = (user) =>
  canEditOwnIPField(user, 'ssm') || canEditOwnIPField(user, 'su');

export const canEditAnyIPForEmployeeCompany = (user, employeeCompany) =>
  canEditIPFieldForEmployeeCompany(user, employeeCompany, 'ssm') ||
  canEditIPFieldForEmployeeCompany(user, employeeCompany, 'su');
